import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Send, MessageCircle, ArrowLeft, Phone, Video, X, Mic, MicOff, VideoOff, PhoneOff, Smile, CheckCheck, Search, PhoneIncoming, Image as ImageIcon, BellOff, BellRing, Flag, MoreVertical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const quickEmojis = ['\u2764\ufe0f', '\ud83d\udc4d', '\ud83d\ude4f', '\ud83d\ude0a', '\ud83c\udf89', '\ud83d\udcaa', '\ud83e\udd1d', '\u2728'];

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'turn:openrelay.metered.ca:80', username: 'openrelayproject', credential: 'openrelayproject' },
  { urls: 'turn:openrelay.metered.ca:443?transport=tcp', username: 'openrelayproject', credential: 'openrelayproject' },
  { urls: 'turns:openrelay.metered.ca:443?transport=tcp', username: 'openrelayproject', credential: 'openrelayproject' }
];

// ===== SOUND UTILITIES =====
const playSound = (type) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    if (type === 'message') {
      osc.frequency.value = 800;
      gain.gain.value = 0.15;
      osc.start();
      osc.stop(ctx.currentTime + 0.1);
      setTimeout(() => {
        const osc2 = ctx.createOscillator();
        const g2 = ctx.createGain();
        osc2.connect(g2); g2.connect(ctx.destination);
        osc2.frequency.value = 1000;
        g2.gain.value = 0.12;
        osc2.start(); osc2.stop(ctx.currentTime + 0.12);
      }, 120);
    } else if (type === 'call') {
      osc.type = 'sine';
      osc.frequency.value = 440;
      gain.gain.value = 0.25;
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc.stop(ctx.currentTime + 0.5);
      // Second ring
      setTimeout(() => {
        const o2 = ctx.createOscillator();
        const g2 = ctx.createGain();
        o2.connect(g2); g2.connect(ctx.destination);
        o2.frequency.value = 523;
        g2.gain.value = 0.25;
        o2.start();
        g2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        o2.stop(ctx.currentTime + 0.5);
      }, 600);
    }
  } catch { /* ignore audio context errors */ }
};

// ===== CALL OVERLAY (WebRTC) =====
const CallOverlay = ({ type, callId, otherUser, token, isInitiator, onEnd }) => {
  const [callStatus, setCallStatus] = useState(isInitiator ? 'ringing' : 'connecting');
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const timerRef = useRef(null);
  const localStreamRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const ringtoneRef = useRef(null);
  const callStatusRef = useRef(callStatus);

  useEffect(() => { callStatusRef.current = callStatus; }, [callStatus]);

  // Play ringtone while ringing
  useEffect(() => {
    if (callStatus === 'ringing' || callStatus === 'connecting') {
      const playRing = () => playSound('call');
      playRing();
      ringtoneRef.current = setInterval(playRing, 2000);
    } else {
      if (ringtoneRef.current) clearInterval(ringtoneRef.current);
    }
    return () => { if (ringtoneRef.current) clearInterval(ringtoneRef.current); };
  }, [callStatus]);

  useEffect(() => {
    setupCall();
    return () => cleanup();
  }, []);

  const cleanup = () => {
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    pcRef.current?.close();
    wsRef.current?.close();
    if (timerRef.current) clearInterval(timerRef.current);
    if (ringtoneRef.current) clearInterval(ringtoneRef.current);
  };

  const setupCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: type === 'video'
      });
      localStreamRef.current = stream;
      if (localVideoRef.current && type === 'video') {
        localVideoRef.current.srcObject = stream;
      }

      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      pcRef.current = pc;

      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // Handle remote tracks - CRITICAL: use separate audio element for reliable audio playback
      pc.ontrack = (event) => {
        const remoteStream = event.streams[0];
        if (event.track.kind === 'video' && remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteStream;
        }
        if (event.track.kind === 'audio' && remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = remoteStream;
          remoteAudioRef.current.play().catch(() => {});
        }
        // Also set on video element for video calls
        if (remoteVideoRef.current && type === 'video') {
          remoteVideoRef.current.srcObject = remoteStream;
        }
        setCallStatus('connected');
        if (!timerRef.current) {
          timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
        }
      };

      const ws = new WebSocket(`${WS_URL}/api/ws/signal/${callId}?token=${token}`);
      wsRef.current = ws;

      ws.onopen = async () => {
        pc.onicecandidate = (event) => {
          if (event.candidate && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ice-candidate', candidate: event.candidate }));
          }
        };
        if (isInitiator) {
          const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: type === 'video' });
          await pc.setLocalDescription(offer);
          ws.send(JSON.stringify({ type: 'offer', sdp: offer }));
        }
      };

      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'offer') {
          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          ws.send(JSON.stringify({ type: 'answer', sdp: answer }));
        } else if (data.type === 'answer') {
          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        } else if (data.type === 'ice-candidate' && data.candidate) {
          try { await pc.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch {}
        } else if (data.type === 'call-ended') {
          cleanup(); onEnd();
        }
      };

      ws.onerror = () => setCallStatus('failed');

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
          handleEndCall();
        }
      };

      if (isInitiator) {
        setTimeout(() => {
          if (callStatusRef.current === 'ringing') setCallStatus('no-answer');
        }, 30000);
      }
    } catch (err) {
      console.error('Call setup error:', err);
      toast.error('Impossibile accedere a microfono/camera');
      setCallStatus('failed');
    }
  };

  const handleEndCall = async () => {
    try {
      await axios.post(`${API_URL}/api/calls/${callId}/end`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    cleanup(); onEnd();
  };

  const toggleMute = () => {
    localStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setIsMuted(!isMuted);
  };
  const toggleCamera = () => {
    localStreamRef.current?.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
    setIsCameraOff(!isCameraOff);
  };

  const fmt = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
  const statusText = {
    ringing: type === 'video' ? 'Videochiamata in corso...' : 'Chiamata in corso...',
    connecting: 'Connessione...',
    connected: fmt(duration),
    failed: 'Connessione fallita',
    ended: 'Chiamata terminata',
    'no-answer': 'Nessuna risposta'
  };

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-b from-stone-900 via-stone-800 to-stone-900 flex flex-col" data-testid="call-overlay">
      {/* Hidden audio element for reliable remote audio playback */}
      <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />

      {/* Remote video - TRUE fullscreen */}
      {type === 'video' && (
        <video ref={remoteVideoRef} autoPlay playsInline
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${callStatus === 'connected' ? 'opacity-100' : 'opacity-0'}`} />
      )}

      {/* Local video PiP - always visible during video call */}
      {type === 'video' && (
        <div className="absolute bottom-28 right-4 w-32 h-44 sm:w-36 sm:h-48 bg-stone-800 rounded-2xl overflow-hidden shadow-2xl border-2 border-white/20 z-30 transition-all">
          <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
          {isCameraOff && <div className="absolute inset-0 bg-stone-800 flex items-center justify-center"><VideoOff className="h-8 w-8 text-white/30" /></div>}
        </div>
      )}

      {/* Avatar overlay (shown when not connected or audio call) */}
      <div className={`flex-1 flex items-center justify-center relative z-10 transition-opacity duration-500 ${type === 'video' && callStatus === 'connected' ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        <div className="text-center">
          <div className={`w-36 h-36 rounded-full mx-auto mb-6 flex items-center justify-center transition-all duration-1000 ${
            callStatus === 'connected' ? 'ring-4 ring-emerald-400/40 shadow-[0_0_60px_rgba(52,211,153,0.2)]' : 'ring-4 ring-white/10 animate-pulse'
          }`}>
            <Avatar className="h-28 w-28">
              <AvatarImage src={otherUser?.picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${otherUser?.user_id}`} />
              <AvatarFallback className="text-4xl bg-amber-500 text-white">{otherUser?.name?.[0]}</AvatarFallback>
            </Avatar>
          </div>
          <h2 className="text-white text-2xl font-bold">{otherUser?.name}</h2>
          <p className="text-white/50 mt-2 text-lg">{statusText[callStatus] || statusText.connecting}</p>
        </div>
      </div>

      {/* Connected status on video (top bar) */}
      {type === 'video' && callStatus === 'connected' && (
        <div className="absolute top-6 left-0 right-0 z-20 flex justify-center">
          <div className="bg-black/40 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-white text-sm font-medium">{otherUser?.name}</span>
            <span className="text-white/60 text-sm">{fmt(duration)}</span>
          </div>
        </div>
      )}

      <div className="p-8 pb-12 flex items-center justify-center gap-6 relative z-10">
        <button onClick={toggleMute} data-testid="toggle-mute-btn"
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isMuted ? 'bg-red-500 text-white scale-110' : 'bg-white/10 text-white hover:bg-white/20'}`}>
          {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </button>
        {type === 'video' && (
          <button onClick={toggleCamera} data-testid="toggle-camera-btn"
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isCameraOff ? 'bg-red-500 text-white scale-110' : 'bg-white/10 text-white hover:bg-white/20'}`}>
            {isCameraOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
          </button>
        )}
        <button onClick={handleEndCall} data-testid="end-call-btn"
          className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg shadow-red-500/40 transition-all hover:scale-105">
          <PhoneOff className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
};

// ===== INCOMING CALL BANNER =====
const IncomingCallBanner = ({ call, onAccept, onReject }) => {
  // Play ringtone for incoming call
  useEffect(() => {
    playSound('call');
    const interval = setInterval(() => playSound('call'), 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div initial={{ y: -100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -100, opacity: 0 }}
      className="fixed top-0 left-0 right-0 z-50 p-4" data-testid="incoming-call-banner">
      <div className="max-w-md mx-auto bg-stone-900 rounded-2xl shadow-2xl border border-white/10 p-5">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar className="h-14 w-14 ring-2 ring-emerald-400/50">
              <AvatarImage src={call.caller_picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${call.caller_id}`} />
              <AvatarFallback className="bg-amber-500 text-white text-lg">{call.caller_name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center animate-pulse">
              <PhoneIncoming className="h-3 w-3 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold truncate">{call.caller_name}</p>
            <p className="text-white/50 text-sm">{call.type === 'video' ? 'Videochiamata in arrivo...' : 'Chiamata in arrivo...'}</p>
          </div>
          <div className="flex gap-3">
            <button onClick={onReject} data-testid="reject-call-btn"
              className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-all">
              <PhoneOff className="h-5 w-5" />
            </button>
            <button onClick={onAccept} data-testid="accept-call-btn"
              className="w-12 h-12 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center transition-all animate-pulse">
              <Phone className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ===== CHAT PAGE =====
const Chat = () => {
  const { token, user } = useAuth();
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [searchConv, setSearchConv] = useState('');
  const [chatSoundEnabled, setChatSoundEnabled] = useState(() => sessionStorage.getItem('mf_chat_sound') !== 'off');
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const typingTimerRef = useRef(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const selectedRef = useRef(null);
  const activeCallRef = useRef(null);
  const headersRef = useRef({ Authorization: `Bearer ${token}` });
  const prevMsgCountRef = useRef(0);
  const imageInputRef = useRef(null);

  useEffect(() => { selectedRef.current = selected; }, [selected]);
  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);
  useEffect(() => { headersRef.current = { Authorization: `Bearer ${token}` }; }, [token]);

  const toggleChatSound = () => {
    const newVal = !chatSoundEnabled;
    setChatSoundEnabled(newVal);
    sessionStorage.setItem('mf_chat_sound', newVal ? 'on' : 'off');
    toast.success(newVal ? (language === 'it' ? 'Notifiche sonore attivate' : 'Sound notifications on') : (language === 'it' ? 'Notifiche sonore disattivate' : 'Sound notifications off'));
  };

  const fetchConversations = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await axios.get(`${API_URL}/api/conversations`, { headers: headersRef.current });
      setConversations(res.data);
    } catch {} finally { if (!silent) setLoading(false); }
  }, []);

  const fetchMessages = useCallback(async (cid, silent = false) => {
    try {
      const res = await axios.get(`${API_URL}/api/conversations/${cid}/messages`, { headers: headersRef.current });
      setMessages(prev => {
        // Play sound for new messages from others
        if (silent && res.data.length > prev.length) {
          const newest = res.data[res.data.length - 1];
          if (newest && newest.sender_id !== user?.user_id && sessionStorage.getItem('mf_chat_sound') !== 'off') {
            playSound('message');
          }
        }
        return (!silent || res.data.length !== prev.length) ? res.data : prev;
      });
    } catch {}
  }, [user?.user_id]);

  const checkIncomingCalls = useCallback(async () => {
    if (activeCallRef.current) return;
    try {
      const res = await axios.get(`${API_URL}/api/calls/pending`, { headers: headersRef.current });
      if (res.data && res.data.call_id) {
        setIncomingCall(res.data);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchConversations();
    pollRef.current = setInterval(() => {
      const sel = selectedRef.current;
      if (sel) {
        fetchMessages(sel.conversation_id, true);
        // Check typing indicator
        axios.get(`${API_URL}/api/conversations/${sel.conversation_id}/typing`, { headers: headersRef.current })
          .then(res => setIsOtherTyping(res.data?.is_typing || false)).catch(() => {});
      }
      fetchConversations(true);
      checkIncomingCalls();
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetchConversations, fetchMessages, checkIncomingCalls]);

  useEffect(() => {
    if (location.state?.conversationId && conversations.length) {
      const c = conversations.find(cv => cv.conversation_id === location.state.conversationId);
      if (c) { setSelected(c); fetchMessages(c.conversation_id); }
    }
  }, [location.state, conversations, fetchMessages]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSelect = (c) => {
    setSelected(c);
    fetchMessages(c.conversation_id);
    // Re-fetch conversations to update unread count
    setTimeout(() => fetchConversations(true), 500);
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!newMsg.trim() || !selected || sending) return;
    const other = getOther(selected);
    if (!other) return;
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/conversations/${selected.conversation_id}/messages`,
        { receiver_id: other.user_id, content: newMsg.trim() }, { headers: headersRef.current });
      setMessages(p => [...p, res.data]);
      setNewMsg('');
      setShowEmoji(false);
      inputRef.current?.focus();
      fetchConversations(true);
    } catch {} finally { setSending(false); }
  };

  const handleImageSend = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'it' ? 'File troppo grande (max 5MB)' : 'File too large (max 5MB)');
      return;
    }
    const other = getOther(selected);
    if (!other) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const isVideo = file.type.startsWith('video/');
      const content = isVideo ? `[video]${reader.result}[/video]` : `[img]${reader.result}[/img]`;
      setSending(true);
      try {
        const res = await axios.post(`${API_URL}/api/conversations/${selected.conversation_id}/messages`,
          { receiver_id: other.user_id, content }, { headers: headersRef.current });
        setMessages(p => [...p, res.data]);
        fetchConversations(true);
      } catch {
        toast.error(language === 'it' ? 'Errore invio media' : 'Error sending media');
      } finally { setSending(false); }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const sendEmoji = (emoji) => {
    setNewMsg(prev => prev + emoji);
    inputRef.current?.focus();
  };

  const sendTypingSignal = () => {
    if (!selected) return;
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    axios.post(`${API_URL}/api/conversations/${selected.conversation_id}/typing`, {}, { headers: headersRef.current }).catch(() => {});
    typingTimerRef.current = setTimeout(() => {}, 3000);
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm(language === 'it' ? 'Eliminare questo messaggio?' : 'Delete this message?')) return;
    try {
      await axios.delete(`${API_URL}/api/messages/${messageId}`, { headers: headersRef.current });
      setMessages(prev => prev.map(m => m.message_id === messageId ? { ...m, content: '[Messaggio eliminato]', deleted: true } : m));
    } catch { toast.error('Error'); }
  };

  const startCall = async (callType) => {
    const other = getOther(selected);
    try {
      const res = await axios.post(`${API_URL}/api/calls/initiate`,
        { conversation_id: selected.conversation_id, type: callType },
        { headers: headersRef.current });
      await axios.post(`${API_URL}/api/conversations/${selected.conversation_id}/messages`,
        { receiver_id: other.user_id, content: callType === 'video' ? '[Videochiamata avviata]' : '[Chiamata audio avviata]' },
        { headers: headersRef.current }).catch(() => {});
      setActiveCall({ type: callType, callId: res.data.call_id, otherUser: other, isInitiator: true });
    } catch (err) {
      toast.error(err.response?.status === 409 ? (language === 'it' ? 'Chiamata già attiva' : 'Call already active') : (language === 'it' ? 'Errore nell\'avvio della chiamata' : 'Error starting call'));
    }
  };

  const acceptIncomingCall = async () => {
    if (!incomingCall) return;
    try {
      await axios.post(`${API_URL}/api/calls/${incomingCall.call_id}/answer`, {}, { headers: headersRef.current });
      setActiveCall({ type: incomingCall.type, callId: incomingCall.call_id, otherUser: { user_id: incomingCall.caller_id, name: incomingCall.caller_name, picture: incomingCall.caller_picture }, isInitiator: false });
      setIncomingCall(null);
    } catch { toast.error(language === 'it' ? 'Errore nella risposta' : 'Error answering'); }
  };

  const rejectIncomingCall = async () => {
    if (!incomingCall) return;
    try { await axios.post(`${API_URL}/api/calls/${incomingCall.call_id}/reject`, {}, { headers: headersRef.current }); } catch {}
    setIncomingCall(null);
  };

  const handleReportChat = async (reason) => {
    if (!selected || !reason) return;
    try {
      await axios.post(`${API_URL}/api/conversations/${selected.conversation_id}/report`,
        { reason, description: reason },
        { headers: headersRef.current });
      toast.success(language === 'it' ? 'Segnalazione inviata' : 'Report sent');
      setShowReportDialog(false);
      setReportReason('');
    } catch { toast.error('Error'); }
  };

  const getOther = (c) => c?.participants?.find(p => p.user_id !== user?.user_id);

  const filteredConversations = conversations.filter(c => {
    if (!searchConv) return true;
    const other = getOther(c);
    return other?.name?.toLowerCase().includes(searchConv.toLowerCase());
  });

  const formatTime = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return language === 'it' ? 'Ieri' : 'Yesterday';
    return d.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
  };

  // Render message content (text, image, or video)
  const renderMessageContent = (content) => {
    if (content.startsWith('[img]') && content.endsWith('[/img]')) {
      const src = content.slice(5, -6);
      return <img src={src} alt="" className="max-w-[240px] max-h-[200px] rounded-xl object-cover cursor-pointer" onClick={() => window.open(src, '_blank')} />;
    }
    if (content.startsWith('[video]') && content.endsWith('[/video]')) {
      const src = content.slice(7, -8);
      return <video src={src} controls className="max-w-[240px] max-h-[200px] rounded-xl" />;
    }
    return <p className="break-words overflow-wrap-anywhere text-[14px] leading-relaxed" style={{overflowWrap: 'anywhere', wordBreak: 'break-word'}}>{content}</p>;
  };

  if (activeCall) return <CallOverlay type={activeCall.type} callId={activeCall.callId} otherUser={activeCall.otherUser} token={token} isInitiator={activeCall.isInitiator} onEnd={() => setActiveCall(null)} />;

  return (
    <div className="min-h-screen bg-stone-50 overflow-hidden">
      <Navbar />
      <AnimatePresence>
        {incomingCall && !activeCall && (
          <IncomingCallBanner call={incomingCall} onAccept={acceptIncomingCall} onReject={rejectIncomingCall} />
        )}
      </AnimatePresence>
      <main className="max-w-6xl mx-auto px-0 sm:px-4 lg:px-8 py-0 sm:py-4">
        <div className="bg-white sm:rounded-2xl shadow-sm border-0 sm:border border-stone-200 overflow-hidden h-[calc(100vh-64px)] sm:h-[calc(100vh-96px)]">
          <div className="flex h-full">

            {/* SIDEBAR */}
            <div className={`w-full sm:w-80 lg:w-96 border-r border-stone-100 flex flex-col bg-white ${selected ? 'hidden sm:flex' : ''}`}>
              <div className="p-4 pb-3">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading text-xl font-bold text-stone-900">{t('chat')}</h2>
                  <div className="flex items-center gap-2">
                    <button onClick={toggleChatSound} data-testid="toggle-chat-sound"
                      className={`p-1.5 rounded-lg transition-colors ${chatSoundEnabled ? 'text-amber-600 hover:bg-amber-50' : 'text-stone-400 hover:bg-stone-50'}`}
                      title={chatSoundEnabled ? 'Disattiva suoni' : 'Attiva suoni'}>
                      {chatSoundEnabled ? <BellRing className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                    </button>
                    <Badge className="bg-amber-100 text-amber-700 text-xs">{conversations.length}</Badge>
                  </div>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <input data-testid="chat-search" value={searchConv} onChange={e => setSearchConv(e.target.value)}
                    placeholder={t('search') + '...'} className="w-full pl-9 pr-4 py-2.5 bg-stone-50 rounded-xl text-sm border-0 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:bg-white transition-all" />
                </div>
              </div>

              {loading ? (
                <div className="flex-1 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" /></div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center mb-4">
                    <MessageCircle className="h-10 w-10 text-amber-300" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-stone-700 mb-1">{language === 'it' ? 'Nessuna chat' : 'No chats yet'}</h3>
                  <p className="text-stone-400 text-sm mb-4">{language === 'it' ? 'Vai su una richiesta e clicca il pulsante Chat per iniziare' : 'Go to a request and click Chat to start'}</p>
                  <Link to="/feed"><Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-5">{language === 'it' ? 'Esplora richieste' : 'Explore requests'}</Button></Link>
                </div>
              ) : (
                <ScrollArea className="flex-1">
                  {filteredConversations.map(conv => {
                    const other = getOther(conv);
                    if (!other) return null;
                    const isConvActive = selected?.conversation_id === conv.conversation_id;
                    return (
                      <button key={conv.conversation_id} onClick={() => handleSelect(conv)} data-testid={`conv-${conv.conversation_id}`}
                        className={`w-full px-4 py-3 flex items-center gap-3 transition-all border-b border-stone-50 ${isConvActive ? 'bg-amber-50' : 'hover:bg-stone-50'}`}>
                        <Avatar className="h-12 w-12 shrink-0">
                          <AvatarImage src={other.picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${other.user_id}`} />
                          <AvatarFallback className="bg-amber-100 text-amber-700">{other.name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 text-left min-w-0">
                          <div className="flex justify-between items-baseline">
                            <p className={`font-semibold text-sm truncate ${isConvActive ? 'text-amber-800' : 'text-stone-900'}`}>{other.name}</p>
                            {conv.last_message_at && <span className="text-[10px] text-stone-400 shrink-0 ml-2">{formatTime(conv.last_message_at)}</span>}
                          </div>
                          {conv.last_message && <p className="text-xs text-stone-500 truncate mt-0.5">{conv.last_message}</p>}
                        </div>
                        {conv.unread_count > 0 && (
                          <Badge className="bg-amber-500 text-white text-[10px] h-5 min-w-[20px] p-0 flex items-center justify-center rounded-full shrink-0">{conv.unread_count}</Badge>
                        )}
                      </button>
                    );
                  })}
                </ScrollArea>
              )}
            </div>

            {/* MESSAGES AREA */}
            <div className={`flex-1 flex flex-col ${!selected ? 'hidden sm:flex' : ''}`} style={{ background: 'transparent' }}>
              {selected ? (
                <>
                  <div className="px-4 py-3 border-b border-stone-200 bg-white flex items-center gap-3 shadow-sm relative z-10">
                    <button className="sm:hidden p-1.5 hover:bg-stone-100 rounded-lg transition-colors" onClick={() => setSelected(null)}>
                      <ArrowLeft className="h-5 w-5 text-stone-600" />
                    </button>
                    <Avatar className="h-10 w-10 shrink-0 ring-2 ring-stone-100">
                      <AvatarImage src={getOther(selected)?.picture} />
                      <AvatarFallback className="bg-amber-100 text-amber-700">{getOther(selected)?.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-stone-900 text-sm truncate">{getOther(selected)?.name}</h3>
                      <p className="text-[11px] text-emerald-600">Online</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button data-testid="audio-call-btn" variant="ghost" size="icon" onClick={() => startCall('audio')}
                        className="h-10 w-10 rounded-full text-stone-500 hover:text-emerald-700 hover:bg-emerald-50 transition-all">
                        <Phone className="h-[18px] w-[18px]" />
                      </Button>
                      <Button data-testid="video-call-btn" variant="ghost" size="icon" onClick={() => startCall('video')}
                        className="h-10 w-10 rounded-full text-stone-500 hover:text-blue-700 hover:bg-blue-50 transition-all">
                        <Video className="h-[18px] w-[18px]" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full text-stone-400 hover:text-stone-600">
                            <MoreVertical className="h-[18px] w-[18px]" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setShowReportDialog(true)} className="text-red-600">
                            <Flag className="h-4 w-4 mr-2" />
                            {language === 'it' ? 'Segnala chat' : 'Report chat'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Messages with chat background */}
                  <ScrollArea className="flex-1 chat-bg">
                    <div className="px-4 py-3 space-y-1">
                      {messages.length === 0 && (
                        <div className="text-center py-12">
                          <p className="text-stone-400 text-sm">{language === 'it' ? 'Inizia la conversazione! Scrivi un messaggio.' : 'Start the conversation! Write a message.'}</p>
                        </div>
                      )}
                      {messages.map((msg, i) => {
                        const isOwn = msg.sender_id === user?.user_id;
                        const isSystem = msg.content?.startsWith('[') && msg.content?.endsWith(']') && !msg.content?.startsWith('[img]') && !msg.content?.startsWith('[video]');
                        const showAvatar = !isOwn && (i === 0 || messages[i-1]?.sender_id !== msg.sender_id);
                        const isDeleted = msg.deleted || msg.content === '[Messaggio eliminato]';

                        if (isSystem) {
                          return (
                            <div key={msg.message_id} className="flex justify-center py-2">
                              <span className="text-[11px] text-stone-400 bg-stone-100 px-4 py-1.5 rounded-full">{msg.content.slice(1, -1)}</span>
                            </div>
                          );
                        }

                        return (
                          <motion.div key={msg.message_id} initial={{ opacity: 0, y: 8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.15 }}
                            className={`flex items-end gap-2 group ${isOwn ? 'justify-end' : 'justify-start'} ${showAvatar ? 'mt-3' : 'mt-0.5'}`}>
                            {!isOwn && showAvatar && (
                              <Avatar className="h-7 w-7 shrink-0">
                                <AvatarImage src={getOther(selected)?.picture} />
                                <AvatarFallback className="text-[10px] bg-amber-100 text-amber-700">{getOther(selected)?.name?.[0]}</AvatarFallback>
                              </Avatar>
                            )}
                            {!isOwn && !showAvatar && <div className="w-7" />}
                            {/* Delete button for own messages */}
                            {isOwn && !isDeleted && (
                              <button onClick={() => handleDeleteMessage(msg.message_id)}
                                className="p-1 rounded-full text-stone-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all self-center"
                                title={language === 'it' ? 'Elimina' : 'Delete'}>
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <div className={`max-w-[70%] px-4 py-2.5 ${isDeleted
                              ? 'bg-stone-100 text-stone-400 rounded-2xl italic text-sm'
                              : isOwn
                                ? 'bg-amber-500 text-white rounded-2xl rounded-br-md shadow-sm'
                                : 'bg-white text-stone-800 rounded-2xl rounded-bl-md shadow-sm border border-stone-100'
                            }`}>
                              {isDeleted
                                ? <p className="text-[13px]">{language === 'it' ? 'Messaggio eliminato' : 'Message deleted'}</p>
                                : renderMessageContent(msg.content)}
                              <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? 'text-amber-100' : 'text-stone-400'}`}>
                                <span className="text-[10px]">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                {isOwn && !isDeleted && <CheckCheck className="h-3 w-3" />}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                      {/* Typing indicator */}
                      {isOtherTyping && (
                        <div className="flex items-end gap-2 mt-2">
                          <Avatar className="h-7 w-7 shrink-0">
                            <AvatarImage src={getOther(selected)?.picture} />
                            <AvatarFallback className="text-[10px] bg-amber-100 text-amber-700">{getOther(selected)?.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-stone-100">
                            <div className="flex gap-1">
                              <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={endRef} />
                    </div>
                  </ScrollArea>

                  <AnimatePresence>
                    {showEmoji && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        className="bg-white border-t border-stone-100 px-4 py-2 overflow-hidden">
                        <div className="flex gap-2 flex-wrap">
                          {quickEmojis.map(em => (
                            <button key={em} onClick={() => sendEmoji(em)} className="text-2xl hover:scale-125 transition-transform p-1">{em}</button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <form onSubmit={handleSend} className="p-3 bg-white border-t border-stone-100">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => setShowEmoji(!showEmoji)}
                        className={`p-2.5 rounded-full transition-colors ${showEmoji ? 'bg-amber-100 text-amber-600' : 'text-stone-400 hover:text-stone-600 hover:bg-stone-50'}`}>
                        <Smile className="h-5 w-5" />
                      </button>
                      <input type="file" accept="image/*,video/*" onChange={handleImageSend} ref={imageInputRef} className="hidden" />
                      <button type="button" data-testid="attach-media-btn" onClick={() => imageInputRef.current?.click()}
                        className="p-2.5 rounded-full text-stone-400 hover:text-stone-600 hover:bg-stone-50 transition-colors">
                        <ImageIcon className="h-5 w-5" />
                      </button>
                      <input ref={inputRef} data-testid="message-input" placeholder={t('typeMessage')}
                        className="flex-1 min-w-0 py-3 px-4 bg-stone-50 rounded-2xl text-sm border-0 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:bg-white transition-all"
                        value={newMsg} onChange={e => { setNewMsg(e.target.value); sendTypingSignal(); }} />
                      <button type="submit" data-testid="send-message-btn" disabled={!newMsg.trim() || sending}
                        className={`p-3 rounded-full transition-all ${newMsg.trim() ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm' : 'bg-stone-100 text-stone-400'}`}>
                        <Send className="h-5 w-5" />
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center chat-bg">
                  <div className="w-24 h-24 rounded-full bg-amber-50 flex items-center justify-center mb-5">
                    <MessageCircle className="h-12 w-12 text-amber-300" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-stone-700 mb-2">{language === 'it' ? 'Le tue conversazioni' : 'Your conversations'}</h3>
                  <p className="text-stone-400 text-sm max-w-xs">{language === 'it' ? 'Seleziona una chat dalla lista o vai su una richiesta per iniziare a chattare' : 'Select a chat or go to a request to start chatting'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Report Chat Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{language === 'it' ? 'Segnala questa chat' : 'Report this chat'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {[
              { id: 'spam', label: language === 'it' ? 'Spam o pubblicità' : 'Spam or advertising' },
              { id: 'harassment', label: language === 'it' ? 'Molestie o bullismo' : 'Harassment or bullying' },
              { id: 'scam', label: language === 'it' ? 'Truffa o frode' : 'Scam or fraud' },
              { id: 'inappropriate', label: language === 'it' ? 'Contenuto inappropriato' : 'Inappropriate content' },
              { id: 'other', label: language === 'it' ? 'Altro' : 'Other' }
            ].map(r => (
              <button key={r.id} onClick={() => handleReportChat(r.label)} data-testid={`report-reason-${r.id}`}
                className="w-full text-left p-3 rounded-xl border border-stone-200 hover:border-red-300 hover:bg-red-50 text-sm transition-all">
                {r.label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Chat;
